/* ==========================================================================
   VOOSH — Motion core

   Small on purpose. This handles scroll reveals and flavour theming for every
   page, with no dependencies. GSAP/Lenis are heavy and only earn their weight
   on pages with pinned or scrubbed sections, so they are loaded on demand by
   the sections that need them — never globally.

   Public API (window.VOOSH):
     .reduced                 → boolean, live-updating
     .observe(el)             → register an element for scroll reveal
     .setFlavour(slug, root)  → re-theme a region
     .onFlavour(fn)           → subscribe to flavour changes
   ========================================================================== */

(function () {
  'use strict';

  var root = document.documentElement;

  // Flip no-js → js immediately so reveal styles can take over before paint.
  root.classList.remove('no-js');
  root.classList.add('js');

  var motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = motionQuery.matches;

  var VOOSH = {
    reduced: reduced,
    FLAVOURS: ['watermelon', 'passionfruit', 'kiwi-lime', 'unflavoured', 'none']
  };

  /* --- Scroll reveal -------------------------------------------------------
     One shared observer for the whole page. Elements unobserve after firing,
     so this stays cheap no matter how long the page gets.

     When reduced motion is on we don't observe at all — we mark everything
     visible up front. The CSS already collapses the transform, so the result
     is the composed final layout rather than "animations off". */

  var observer = null;

  function revealNow(el) {
    el.classList.add('is-in');
  }

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          revealNow(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        // Fire slightly before the element reaches the viewport edge so the
        // reveal is already settling by the time it's properly in view.
        rootMargin: '0px 0px -12% 0px',
        threshold: 0.01
      }
    );

    return observer;
  }

  VOOSH.observe = function (el) {
    if (!el) return;
    if (reduced || !('IntersectionObserver' in window)) {
      revealNow(el);
      return;
    }
    getObserver().observe(el);
  };

  VOOSH.scan = function (scope) {
    var container = scope || document;
    container.querySelectorAll('[data-reveal]:not(.is-in)').forEach(VOOSH.observe);
  };

  /* --- Flavour theming -----------------------------------------------------
     Setting the attribute is the entire mechanism — CSS custom properties
     cascade the new accent to every descendant, and @property makes the
     change cross-fade instead of snap. No per-element JS updates, which is
     why a full-viewport flavour switch costs nothing at runtime. */

  var flavourListeners = [];

  VOOSH.setFlavour = function (slug, scope) {
    var target = scope || root;
    var next = VOOSH.FLAVOURS.indexOf(slug) === -1 ? 'none' : slug;

    if (target.getAttribute('data-flavour') === next) return next;

    target.setAttribute('data-flavour', next);

    flavourListeners.forEach(function (fn) {
      try {
        fn(next, target);
      } catch (err) {
        // A broken subscriber must not stop the theme from applying.
        console.error('[VOOSH] flavour listener failed', err);
      }
    });

    return next;
  };

  VOOSH.onFlavour = function (fn) {
    if (typeof fn === 'function') flavourListeners.push(fn);
  };

  /* --- Lazy loader for heavy motion libraries ------------------------------
     Sections call VOOSH.loadGSAP() only when they're actually present on the
     page. Repeat calls share one promise, so two pinned sections don't pull
     the library twice. */

  var gsapPromise = null;

  VOOSH.loadGSAP = function (urls) {
    if (gsapPromise) return gsapPromise;

    // Reduced-motion users never need the scroll engine at all — skipping the
    // download is both a performance and an accessibility win.
    if (reduced) {
      gsapPromise = Promise.resolve(null);
      return gsapPromise;
    }

    gsapPromise = urls.reduce(function (chain, url) {
      return chain.then(function () {
        return new Promise(function (resolve, reject) {
          var script = document.createElement('script');
          script.src = url;
          script.async = false;
          script.onload = resolve;
          script.onerror = function () {
            reject(new Error('[VOOSH] failed to load ' + url));
          };
          document.head.appendChild(script);
        });
      });
    }, Promise.resolve());

    return gsapPromise.then(function () {
      return window.gsap || null;
    });
  };

  /* --- Reduced-motion changes at runtime ----------------------------------
     Someone can toggle the OS setting with the page open. Honour it live:
     reveal everything still pending so nothing is left invisible. */

  function onMotionChange(event) {
    reduced = event.matches;
    VOOSH.reduced = reduced;

    if (!reduced) return;

    if (observer) {
      observer.disconnect();
      observer = null;
    }
    document.querySelectorAll('[data-reveal]:not(.is-in)').forEach(revealNow);
  }

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionChange);
  } else if (typeof motionQuery.addListener === 'function') {
    motionQuery.addListener(onMotionChange); // Safari < 14
  }

  /* --- Boot ---------------------------------------------------------------- */

  function init() {
    VOOSH.scan();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme-editor support: re-scan when a section is re-rendered so reveals
  // keep working while the merchant edits, instead of leaving new blocks
  // stuck at opacity 0.
  document.addEventListener('shopify:section:load', function (event) {
    VOOSH.scan(event.target);
  });

  window.VOOSH = VOOSH;
})();
